import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

export default function Home() {
  return (
    <main data-testid="home-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {/* Hero */}
          <motion.div
            {...fade(0)}
            className="lg:col-span-8 relative overflow-hidden rounded-sm border border-[#D1CFC7] min-h-[360px] lg:min-h-[560px] flex items-end"
          >
            <img
              src={IMAGES.hero}
              alt="Foggy forest footpath through Montana timber"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#16271F]/95 via-[#1E352F]/65 to-[#1E352F]/30" />
            <div className="absolute inset-x-0 top-0 h-3/4 backdrop-blur-[3px] [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.95),transparent)] [-webkit-mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.95),transparent)]" />
            <div className="relative p-8 md:p-12 text-[#F8F7F4]">
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#F4D9C6] drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
                Flathead Valley, Montana
              </span>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-medium mt-3 max-w-2xl leading-[1.02]">
                Explore your property for the first time in a new way.
              </h1>
              <p className="mt-4 max-w-xl text-base sm:text-lg text-[#E4E8E2] leading-relaxed">
                Hand &amp; machine built dirt and mulch trails that let you walk
                every corner of your property — and turn the woods you own into your
                own private park. No trail too big, no trail too small.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 bg-[#F8F7F4]/12 border border-[#F8F7F4]/25 backdrop-blur-sm rounded-sm px-4 py-2">
                <span className="font-serif-display text-2xl text-[#F8F7F4] leading-none">20</span>
                <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#E4E8E2]">
                  Years of Flathead Valley experience
                </span>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/quote"
                  data-testid="hero-quote-button"
                  className="inline-flex items-center gap-2 bg-[#F8F7F4] text-[#1E352F] px-6 py-3 rounded-sm font-medium hover:bg-[#A95C42] hover:text-[#F8F7F4] transition-colors duration-300"
                >
                  Request a Quote <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/gallery"
                  data-testid="hero-gallery-button"
                  className="inline-flex items-center gap-2 border border-[#F8F7F4]/60 text-[#F8F7F4] px-6 py-3 rounded-sm font-medium hover:bg-[#F8F7F4]/10 transition-colors duration-300"
                >
                  View Gallery
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Right column */}
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-5">
            <motion.div
              {...fade(0.15)}
              className="relative overflow-hidden rounded-sm border border-[#D1CFC7] min-h-[170px] lg:min-h-[270px] group"
            >
              <img
                src={IMAGES.showcaseTrail}
                alt="Dirt singletrack through dense forest"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </motion.div>

            <motion.div
              {...fade(0.25)}
              className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-6 lg:p-8 flex flex-col justify-center min-h-[170px] lg:min-h-[270px]"
            >
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A95C42]">
                What we do
              </span>
              <p className="font-serif-display text-2xl lg:text-[1.7rem] leading-snug text-[#1E352F] mt-2">
                Make full use of the land you already own — clean dirt & mulch
                trails through your own woods.
              </p>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="mt-4 inline-flex items-center gap-2 text-[#2D4A3E] font-medium hover:gap-3 transition-all"
              >
                Our services <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Slim feature strip */}
        <motion.div
          {...fade(0.35)}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5 mt-4 lg:mt-5"
        >
          {[
            ["Dirt & Mulch", "Natural-surface tread that feels like it has always been there."],
            ["Hand & Machine Built", "Efficient machine work, finished with hand-stacked rock and detail."],
            ["No Job Too Big or Small", "From a short garden path to miles of backcountry trail."],
          ].map(([title, body], i) => (
            <div
              key={title}
              data-testid={`home-feature-${i}`}
              className="rounded-sm border border-[#D1CFC7] bg-[#FFFFFF] p-6"
            >
              <h3 className="font-serif-display text-xl text-[#2D4A3E]">{title}</h3>
              <p className="text-sm text-[#4A5D54] mt-1 leading-relaxed">{body}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </main>
  );
}
